import { useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridColDef, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-premium';
import { CboRole } from '@cbo/shared-library';
import Header from '../components/Header/Header';
import DataGrid, { BaseGridRow } from '../components/DataGrid/DataGrid';
import ActionsDialog from '../components/ActionsDialog/ActionsDialog';
import routes from '../constants/routes';
import { useUsers } from '../contexts/userContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useAccountPreferences } from '../contexts/accountPreferencesContext';
import { formatTimeStamp } from '../admin/utils/siteUtils';
import isPermitted from '../lib/permissions';
import { User } from '../models/User';
import { getFilterOperators } from '../components/DataGrid/helpers';
import CustomActionsMenuCell, {
  DataGridActionsMenuItems,
} from '../components/DataGrid/CustomCells/CustomActionsMenuCell';
import MetricsInUseDialog from './MetricsInUseDialog';
import { useGetAllCustomMetricsQuery } from '../admin/requests/queries';
import { useCustomMetricDeleteMutation } from '../admin/requests/mutations';
import { CustomMetric } from '../admin/requests/index';
import { metricsInUseLinkSx } from './styles';

interface CustomMetricRow extends BaseGridRow {
  id: string;
  name: string;
  description: string;
  type: string;
  metricsInUse: number;
  createdBy: string;
  lastModified: string;
}

const formatCreatedBy = (createdByValue: string | undefined) => {
  const createdBy = createdByValue || 'N/A';
  return createdBy.includes('-') ? createdBy.split('-')[1] : createdBy;
};

function CustomMetricsHomePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useUsers();
  const { setSnackbarState } = useSnackbar();
  const queryClient = useQueryClient();
  const { preferences } = useAccountPreferences();

  // State for pagination (client-side)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: customMetricsData,
    isLoading: isCustomMetricsLoading,
    isFetching: isCustomMetricsFetching,
  } = useGetAllCustomMetricsQuery(
    0, // Always fetch first page
    100 // Hardcoded page size to fetch 100 records
  );

  // State for dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMetricId, setSelectedMetricId] = useState<string>('');
  const [selectedMetricName, setSelectedMetricName] = useState('');
  const [selectedMetricCount, setSelectedMetricCount] = useState(0);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState<{ id: string; name: string } | null>(null);

  // Delete mutation
  const deleteMetricMutation = useCustomMetricDeleteMutation();

  // Map API response to grid rows
  const data: CustomMetricRow[] = useMemo(() => {
    if (!customMetricsData?.pageContent || !Array.isArray(customMetricsData.pageContent)) {
      return [];
    }

    return customMetricsData.pageContent.map((item: CustomMetric) => ({
      id: item.id || '',
      name: item.name || '',
      description: item.description || '',
      type: item.metricType || '',
      metricsInUse: item.usageCount,
      createdBy: item.createdBy || 'N/A',
      lastModified: item.updatedAt ? formatTimeStamp(item.updatedAt, preferences) : '-',
    }));
  }, [customMetricsData, preferences]);

  const isLoading = isCustomMetricsLoading || isCustomMetricsFetching;

  // Check permissions
  const canCreateMetric = isPermitted(user as User, [CboRole.CUSTOM_METRIC_WRITER], false);

  const handleCreateMetricClick = () => {
    history.push(routes.CUSTOM_METRICS_FORM.replace(':metricId', '0'));
  };

  const handleMetricsInUseClick = (metricId: string, metricName: string, count: number) => {
    setSelectedMetricId(metricId);
    setSelectedMetricName(metricName);
    setSelectedMetricCount(count);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleEditMetric = useCallback(
    (metricId: string) => {
      history.push(routes.CUSTOM_METRICS_FORM.replace(':metricId', metricId));
    },
    [history]
  );

  const handleDeleteMetric = useCallback(
    (metricId: string) => {
      const metric = data.find((m) => m.id === metricId);
      if (metric) {
        setMetricToDelete({ id: metricId, name: metric.name });
        setDeleteDialogOpen(true);
      }
    },
    [data]
  );

  const handleConfirmDelete = async () => {
    if (!metricToDelete) return;

    try {
      await deleteMetricMutation.mutateAsync({ id: metricToDelete.id });

      // Invalidate queries to refresh the grid
      queryClient.invalidateQueries({ queryKey: ['allCustomMetrics'] });

      // Show success snackbar
      setSnackbarState({
        open: true,
        message: t('navItems.customMetricsHomePage.deleteSuccess', { metricName: metricToDelete.name }),
        color: 'success',
      });

      // Close dialog
      setDeleteDialogOpen(false);
      setMetricToDelete(null);
    } catch (error) {
      // Show error snackbar
      setSnackbarState({
        open: true,
        message: t('navItems.customMetricsHomePage.deleteError'),
        color: 'error',
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMetricToDelete(null);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('globalFilters.name'),
        flex: 2,
        minWidth: 180,
        filterOperators: getFilterOperators('string', ['contains', 'equals']),
      },
      {
        field: 'description',
        headerName: t('admin.allSitesDatagrid.descriptionColumnHeader'),
        flex: 2.5,
        minWidth: 200,
        filterOperators: getFilterOperators('string', ['contains', 'equals']),
      },
      {
        field: 'type',
        headerName: t('admin.notificationSettingsPage.form.events.type'),
        flex: 1.2,
        minWidth: 120,
        filterOperators: getFilterOperators('singleSelect', ['is']),
        type: 'singleSelect',
        valueOptions: ['Number', 'Currency', 'Percentage'],
      },
      {
        field: 'metricsInUse',
        headerName: t('navItems.customMetricsHomePage.columns.metricsInUse'),
        flex: 1.2,
        minWidth: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>) => {
          const count = params.row.metricsInUse as number;
          const metricId = params.row.id as string;
          const metricName = params.row.name as string;

          if (count === 0) {
            return `${count} ${t('navItems.customMetricsHomePage.reports')}`;
          }

          return (
            <Button
              variant='text'
              onClick={() => handleMetricsInUseClick(metricId, metricName, count)}
              sx={metricsInUseLinkSx}
              data-testid={`metrics-in-use-link-${params.row.id}`}
            >
              {`${count} ${t('navItems.customMetricsHomePage.reports')}`}
            </Button>
          );
        },
      },
      {
        field: 'createdBy',
        headerName: t('sales.journalEntry.columnHeaders.createdBy'),
        flex: 1.5,
        minWidth: 150,
        filterOperators: getFilterOperators('string', ['contains', 'equals']),
        valueFormatter: (value: string) => formatCreatedBy(value),
        renderCell: (params: GridRenderCellParams<GridValidRowModel>) =>
          formatCreatedBy(params.row.createdBy as string | undefined),
      },
      {
        field: 'lastModified',
        headerName: t('inventory.invoicesColumns.lastModified'),
        flex: 1.5,
        minWidth: 150,
        filterOperators: getFilterOperators('string', ['contains', 'equals']),
      },
      {
        field: 'actions',
        headerName: t('navItems.customMetricsHomePage.columns.actions'),
        flex: 0.8,
        minWidth: 90,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>) => {
          const metricsInUse = params.row.metricsInUse as number;
          const isMetricInUse = metricsInUse > 0;

          const actions = [DataGridActionsMenuItems.Edit, DataGridActionsMenuItems.Delete];

          const onClickFunctions = {
            [DataGridActionsMenuItems.Edit]: () => handleEditMetric(params.row.id as string),
            [DataGridActionsMenuItems.Delete]: () => handleDeleteMetric(params.row.id as string),
          };

          const iconOverrides = {
            [DataGridActionsMenuItems.Edit]: <EditIcon fontSize='small' />,
            [DataGridActionsMenuItems.Delete]: <DeleteIcon fontSize='small' />,
          };

          return (
            <CustomActionsMenuCell
              id={params.row.id as string}
              actions={actions}
              onClickFunctions={onClickFunctions}
              iconOverrides={iconOverrides}
              extraPermissions={{
                [DataGridActionsMenuItems.Edit]: canCreateMetric,
                [DataGridActionsMenuItems.Delete]: canCreateMetric,
              }}
              disabled={{
                [DataGridActionsMenuItems.Delete]: isMetricInUse,
              }}
              tooltipTitles={{
                ...(isMetricInUse && {
                  [DataGridActionsMenuItems.Delete]: t('navItems.customMetricsHomePage.deleteDisabledTooltip'),
                }),
              }}
            />
          );
        },
      },
    ],
    [t, canCreateMetric, handleEditMetric, handleDeleteMetric]
  );

  const createMetricButton = canCreateMetric ? (
    <Button
      variant='contained'
      size='large'
      data-testid='create-custom-metric-button'
      onClick={handleCreateMetricClick}
    >
      {t('navItems.customMetricsPage.title')}
    </Button>
  ) : undefined;

  return (
    <>
      <Grid item xs={12} px={4} sx={{ pb: 3 }} data-testid='custom-metrics-home-page'>
        <Header
          dataTestId='custom-metrics-home-header'
          title={t('navItems.customMetrics')}
          description={t('navItems.customMetricsHomePage.description')}
          maxDescriptionWidth='600px'
          headerActions={canCreateMetric ? [createMetricButton] : undefined}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DataGrid
              columns={columns}
              rows={data}
              loading={isLoading}
              itemsPerPage={pageSize}
              pagination
              page={page}
              paginationMode='client'
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              filterMode='client'
              sortingMode='client'
              disableMultipleColumnsFiltering
              showExport={data.length > 0}
              localeText={{
                toolbarQuickFilterPlaceholder: t('navItems.customMetricsHomePage.searchPlaceholder'),
                noRowsLabel: t('navItems.customMetricsHomePage.noRows'),
              }}
              data-testid='custom-metrics-data-grid'
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Reports Dialog */}
      <MetricsInUseDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        metricName={selectedMetricName}
        metricId={selectedMetricId}
        metricCount={selectedMetricCount}
      />

      {/* Delete Confirmation Dialog */}
      <ActionsDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        dataTestId='delete-metric-confirmation-dialog'
        dialogTitle={t('navItems.customMetricsHomePage.deleteConfirmTitle', { metricName: metricToDelete?.name })}
        dialogContentText={t('navItems.customMetricsHomePage.deleteConfirmMessage', {
          metricName: metricToDelete?.name,
        })}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        actionButtonText={t('buttonText.delete')}
        actionButtonColor='error'
        actionButtonVariant='contained'
        onActionClick={handleConfirmDelete}
        disableActionButton={deleteMetricMutation.isLoading}
      />
    </>
  );
}

export default CustomMetricsHomePage;
